import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, CheckCircle2, ClipboardCheck, Database, ExternalLink,
  Loader2, RefreshCw, ShieldCheck, UserRoundCheck, UsersRound,
} from 'lucide-react';
import {
  consumeOfficialTmcOnce,
  getOfficialTmcAuthorizationUrl,
  getOfficialTmcStatus,
  handoffOfficialTmcEvent,
  runOfficialTmcReadinessCheck,
  upsertOfficialTmcAccount,
  type OfficialTmcAccount,
  type OfficialTmcConsumeResult,
  type OfficialTmcEvent,
  type OfficialTmcReadinessState,
  type OfficialTmcStatus,
} from '../services/api';

const FALLBACK_CALLBACK_URL = 'https://xianyu-review.everbetter.top/official-tmc/oauth/callback';

const stateMeta: Record<string, { label: string; tone: string; detail: string }> = {
  ready: { label: '已就绪', tone: 'bg-emerald-100 text-emerald-800', detail: '平台凭据、授权账号和消息主题均已具备。' },
  awaiting_authorization: { label: '等待授权', tone: 'bg-amber-100 text-amber-800', detail: '回调地址已准备好，仍需在平台完成正式授权。' },
  unconfigured: { label: '未配置', tone: 'bg-gray-100 text-gray-700', detail: '隔离环境尚未启用官方通道配置。' },
  unavailable_topic: { label: '主题不可用', tone: 'bg-orange-100 text-orange-800', detail: '当前账号没有可用的官方消息主题。' },
  authorization_expired: { label: '授权已过期', tone: 'bg-rose-100 text-rose-800', detail: '需要在平台重新完成授权后再读取通知。' },
  credential_error: { label: '凭据异常', tone: 'bg-rose-100 text-rose-800', detail: '凭据无法用于官方检查，请在服务器安全配置中核对。' },
  failed_check: { label: '检查失败', tone: 'bg-rose-100 text-rose-800', detail: '最近一次官方检查未通过，可刷新状态后重试。' },
};

const eventMeta: Record<string, { label: string; tone: string }> = {
  pending: { label: '待人工接管', tone: 'bg-amber-100 text-amber-800' },
  handed_off: { label: '已交接', tone: 'bg-blue-100 text-blue-800' },
  confirmed: { label: '已确认', tone: 'bg-emerald-100 text-emerald-800' },
  failed: { label: '处理失败', tone: 'bg-rose-100 text-rose-800' },
};

const compactId = (value: string | undefined) => {
  if (!value) return '—';
  if (value.length <= 14) return value;
  return `${value.slice(0, 7)}…${value.slice(-5)}`;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { hour12: false });
};

const statusLabel = (value: OfficialTmcReadinessState) => stateMeta[value] || {
  label: value || '未知', tone: 'bg-gray-100 text-gray-700', detail: '等待下一次官方状态检查。',
};

const actionResultText = (result: OfficialTmcConsumeResult) =>
  `本次收到 ${result.received} 条；已持久化 ${result.persisted} 条；已确认 ${result.confirmed} 条；重复 ${result.deduplicated} 条；拒绝 ${result.rejected} 条；失败 ${result.failed} 条。`;

const OfficialChannel: React.FC = () => {
  const [status, setStatus] = useState<OfficialTmcStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [consuming, setConsuming] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [handoffId, setHandoffId] = useState<number | null>(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountEnabled, setAccountEnabled] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);

  const load = async (withSpinner = true) => {
    if (withSpinner) setLoading(true);
    setError('');
    try {
      const loaded = await getOfficialTmcStatus();
      setStatus({ ...loaded, accounts: loaded.accounts || [], events: loaded.events || [] });
    } catch (reason) {
      setError(`加载官方通道状态失败：${reason instanceof Error ? reason.message : '请稍后重试'}`);
    } finally {
      if (withSpinner) setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    const handleAuthorizationMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'official-tmc-authorization-failed') {
        setAuthorizing(false);
        setNotice('淘宝官方授权未完成，请查看新标签页中的具体原因。');
        void load(false);
        return;
      }
      if (event.data?.type !== 'official-tmc-authorized') return;
      setAuthorizing(false);
      setNotice('淘宝官方授权已完成，账号状态已刷新。');
      setError('');
      void load(false);
    };
    window.addEventListener('message', handleAuthorizationMessage);
    return () => window.removeEventListener('message', handleAuthorizationMessage);
  }, []);

  const accounts = status?.accounts || [];
  const events = status?.events || [];
  const meta = statusLabel(status?.state || 'unconfigured');
  const callbackUrl = status?.callback_url || FALLBACK_CALLBACK_URL;
  const canConsume = status?.state === 'ready';
  const pendingEvents = useMemo(() => events.filter((event) => event.processing_status === 'pending'), [events]);

  const runCheck = async () => {
    setChecking(true);
    setNotice('');
    setError('');
    try {
      const result = await runOfficialTmcReadinessCheck();
      setNotice(result.message || `官方检查完成：${statusLabel(result.state).label}。`);
      await load(false);
    } catch (reason) {
      setError(`官方检查失败：${reason instanceof Error ? reason.message : '请检查平台授权与服务器配置'}`);
    } finally {
      setChecking(false);
    }
  };

  const saveAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    const platformAccountId = accountId.trim();
    if (!platformAccountId) {
      setError('请填写平台卖家账号标识。');
      return;
    }
    setSavingAccount(true);
    setNotice('');
    setError('');
    try {
      const result = await upsertOfficialTmcAccount({
        platform_account_id: platformAccountId,
        display_name: accountName.trim(),
        enabled: accountEnabled,
      });
      setAccountId('');
      setAccountName('');
      setAccountEnabled(true);
      setNotice(result.message || '官方账号映射已保存。');
      await load(false);
    } catch (reason) {
      setError(`保存账号映射失败：${reason instanceof Error ? reason.message : '请稍后重试'}`);
    } finally {
      setSavingAccount(false);
    }
  };

  const startAuthorization = async () => {
    const authorizationWindow = window.open('about:blank', '_blank');
    if (!authorizationWindow) {
      setError('浏览器阻止了新标签页，请允许本站弹出窗口后重试。');
      return;
    }
    setAuthorizing(true);
    setNotice('');
    setError('');
    try {
      const result = await getOfficialTmcAuthorizationUrl();
      if (!result.authorization_url) throw new Error('平台未返回授权地址');
      authorizationWindow.location.href = result.authorization_url;
    } catch (reason) {
      authorizationWindow.close();
      setError(`无法发起官方授权：${reason instanceof Error ? reason.message : '请刷新后重试'}`);
      setAuthorizing(false);
    }
  };

  const consumeOnce = async () => {
    setConsuming(true);
    setNotice('');
    setError('');
    try {
      const result = await consumeOfficialTmcOnce();
      setNotice(result.message || actionResultText(result));
      await load(false);
    } catch (reason) {
      setError(`本次读取失败：${reason instanceof Error ? reason.message : '请先完成官方授权和主题配置'}`);
    } finally {
      setConsuming(false);
    }
  };

  const handoff = async (eventId: number) => {
    setHandoffId(eventId);
    setNotice('');
    setError('');
    try {
      const result = await handoffOfficialTmcEvent(eventId);
      setNotice(result.message || '事件已标记为人工接管。');
      await load(false);
    } catch (reason) {
      setError(`人工交接失败：${reason instanceof Error ? reason.message : '请刷新后重试'}`);
    } finally {
      setHandoffId(null);
    }
  };

  if (loading) {
    return <div className="p-12 flex items-center justify-center gap-3 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> 加载官方通道状态…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-7 animate-fade-in pb-24">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center shadow-sm"><ShieldCheck className="w-6 h-6" /></div>
          <div>
            <p className="text-xs font-bold tracking-[0.16em] text-violet-600 uppercase">Official channel</p>
            <h2 className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900">官方消息通道</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">仅通过已授权的淘宝开放平台接口读取通知，并生成待人工处理事项。此页不展示平台凭据、授权令牌或消息正文。</p>
          </div>
        </div>
        <div className="flex gap-3 self-start">
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-lg bg-white border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50"><RefreshCw className="w-4 h-4" /> 刷新状态</button>
          <button type="button" onClick={runCheck} disabled={checking} className="inline-flex items-center gap-2 rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-violet-800 disabled:opacity-60"><ClipboardCheck className="w-4 h-4" /> {checking ? '检查中…' : '执行官方检查'}</button>
        </div>
      </header>

      {(notice || error) && <div className={`flex gap-3 rounded-lg border px-4 py-3 text-sm ${error ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
        {error ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{error || notice}</span>
      </div>}

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div><div className="text-sm font-bold text-gray-900">通道状态</div><p className="mt-1 text-sm text-gray-500">{meta.detail}</p></div>
            <span className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${meta.tone}`}>{meta.label}</span>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-4"><div className="text-xs font-medium text-gray-500">官方接口</div><div className="mt-1 font-bold text-gray-900">{status?.enabled ? '已启用' : '未启用'}</div></div>
            <div className="rounded-lg bg-gray-50 p-4"><div className="text-xs font-medium text-gray-500">授权账号</div><div className="mt-1 font-bold text-gray-900">{accounts.length} 个</div></div>
            <div className="rounded-lg bg-gray-50 p-4"><div className="text-xs font-medium text-gray-500">待人工处理</div><div className="mt-1 font-bold text-gray-900">{pendingEvents.length} 项</div></div>
          </div>
          <div className="mt-5 border-t border-gray-100 pt-5">
            <div className="text-xs font-bold tracking-wide text-gray-500 uppercase">已允许消息主题</div>
            <div className="mt-2 flex flex-wrap gap-2">{status?.allowed_topics?.length ? status.allowed_topics.map((topic) => <span key={topic} className="rounded-md bg-violet-50 px-2.5 py-1.5 font-mono text-xs text-violet-800">{topic}</span>) : <span className="text-sm text-gray-400">尚未获取到可用主题</span>}</div>
          </div>
        </div>

        <aside className="rounded-xl border border-violet-100 bg-violet-50/50 p-6">
          <div className="flex items-center gap-2 text-sm font-bold text-violet-950"><ExternalLink className="w-4 h-4" /> 平台授权回调</div>
          <p className="mt-2 text-sm leading-6 text-violet-900/70">在开放平台应用配置中保存下列 HTTPS 回调地址。准备好后，由“开始官方授权”跳转到淘宝官方授权页；授权过程中的 code 和令牌不会在页面显示或保存。</p>
          <code className="mt-4 block break-all rounded-lg border border-violet-100 bg-white px-3 py-3 text-xs text-violet-950">{callbackUrl}</code>
          <div className="mt-4 flex gap-2 text-xs leading-5 text-violet-900/70"><ShieldCheck className="h-4 w-4 shrink-0" /> 授权成功返回后，执行一次“官方检查”，确认主题和账号状态。</div>
          <button type="button" onClick={startAuthorization} disabled={authorizing} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-violet-700 px-3.5 py-2.5 text-sm font-bold text-white transition hover:bg-violet-800 disabled:opacity-60"><ExternalLink className="h-4 w-4" /> {authorizing ? '正在跳转…' : '开始官方授权'}</button>
        </aside>
      </section>

      <section className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"><div><h3 className="text-lg font-extrabold text-gray-900">官方授权账号</h3><p className="mt-1 text-sm text-gray-500">只保存账号标识和显示名称，用于关联平台授权状态。</p></div><UsersRound className="h-5 w-5 text-gray-400" /></div>
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left"><thead className="border-b border-gray-100 text-xs text-gray-500"><tr><th className="px-3 py-3 font-bold">显示名称</th><th className="px-3 py-3 font-bold">账号标识</th><th className="px-3 py-3 font-bold">授权状态</th><th className="px-3 py-3 font-bold">最近检查</th></tr></thead><tbody className="divide-y divide-gray-50">{accounts.length ? accounts.map((account: OfficialTmcAccount) => { const accountMeta = statusLabel(account.authorization_state); return <tr key={account.id} className="text-sm"><td className="px-3 py-3 font-semibold text-gray-900">{account.display_name || '未命名账号'}{!account.enabled && <span className="ml-2 text-xs font-normal text-gray-400">已停用</span>}</td><td className="px-3 py-3 font-mono text-xs text-gray-600" title={account.platform_account_id}>{compactId(account.platform_account_id)}</td><td className="px-3 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${accountMeta.tone}`}>{accountMeta.label}</span></td><td className="px-3 py-3 text-xs text-gray-500">{formatDate(account.last_checked_at)}</td></tr>; }) : <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-400">尚未关联官方授权账号</td></tr>}</tbody></table>
        </div>
        <form onSubmit={saveAccount} className="mt-6 grid grid-cols-1 gap-3 border-t border-gray-100 pt-5 md:grid-cols-[1fr_1fr_auto_auto] md:items-end">
          <label className="block text-sm font-bold text-gray-700">平台卖家账号标识<input value={accountId} onChange={(event) => setAccountId(event.target.value)} className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-100" placeholder="授权返回的卖家标识" autoComplete="off" /></label>
          <label className="block text-sm font-bold text-gray-700">显示名称（可选）<input value={accountName} onChange={(event) => setAccountName(event.target.value)} className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-100" placeholder="便于管理员识别" autoComplete="off" /></label>
          <label className="flex items-center gap-2 pb-3 text-sm font-bold text-gray-700"><input type="checkbox" checked={accountEnabled} onChange={(event) => setAccountEnabled(event.target.checked)} className="h-4 w-4 accent-violet-700" /> 启用</label>
          <button type="submit" disabled={savingAccount} className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-black disabled:opacity-60"><UserRoundCheck className="h-4 w-4" /> {savingAccount ? '保存中…' : '保存账号'}</button>
        </form>
      </section>

      <section className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"><div><h3 className="text-lg font-extrabold text-gray-900">显式读取与人工交接</h3><p className="mt-1 text-sm text-gray-500">一次读取只处理当前官方通知页；持久化成功后才确认。不会自动回复或发送任何买家消息。</p></div><button type="button" onClick={consumeOnce} disabled={!canConsume || consuming} title={canConsume ? '读取一页官方通知' : '通道尚未就绪'} className="inline-flex items-center justify-center gap-2 self-start rounded-lg bg-[#FFE815] px-4 py-2.5 text-sm font-bold text-black transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-50"><Database className="h-4 w-4" /> {consuming ? '读取中…' : '读取一次通知'}</button></div>
        <div className="mt-5 overflow-x-auto"><table className="min-w-full text-left"><thead className="border-b border-gray-100 text-xs text-gray-500"><tr><th className="px-3 py-3 font-bold">主题</th><th className="px-3 py-3 font-bold">账号</th><th className="px-3 py-3 font-bold">接收时间</th><th className="px-3 py-3 font-bold">状态</th><th className="px-3 py-3 text-right font-bold">操作</th></tr></thead><tbody className="divide-y divide-gray-50">{events.length ? events.map((event: OfficialTmcEvent) => { const itemMeta = eventMeta[event.processing_status] || { label: event.processing_status || '未知', tone: 'bg-gray-100 text-gray-700' }; const pending = event.processing_status === 'pending'; return <tr key={event.id} className="text-sm"><td className="max-w-[280px] px-3 py-3 font-mono text-xs text-gray-700" title={event.topic}>{compactId(event.topic)}</td><td className="px-3 py-3 font-mono text-xs text-gray-600" title={event.platform_account_id}>{compactId(event.platform_account_id)}</td><td className="px-3 py-3 text-xs text-gray-500">{formatDate(event.received_at)}</td><td className="px-3 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${itemMeta.tone}`}>{itemMeta.label}</span>{event.error_code && <span className="ml-2 text-xs text-rose-600">{event.error_code}</span>}</td><td className="px-3 py-3 text-right">{pending ? <button type="button" onClick={() => void handoff(event.id)} disabled={handoffId === event.id} className="inline-flex items-center gap-1.5 rounded-md bg-violet-50 px-3 py-2 text-xs font-bold text-violet-800 transition hover:bg-violet-100 disabled:opacity-60">{handoffId === event.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserRoundCheck className="h-3.5 w-3.5" />} 人工接管</button> : <span className="text-xs text-gray-400">—</span>}</td></tr>; }) : <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-gray-400">尚无官方通知事件</td></tr>}</tbody></table></div>
      </section>
    </div>
  );
};

export default OfficialChannel;
